import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Button, Tag, Tooltip, Col, Row, Dropdown, Space } from "antd";
import { Copy, Pencil, Globe, Share2, ChevronDown, Link2 } from "lucide-react";
import { format } from "date-fns";
import { getPrimaryMediaUrl, sanitizeText } from "commons/helper";
import { renderSuccessNotifications } from "helpers/error.helpers";
import { getPascalCase } from "helpers/common.helper";

export const EVENT_STATUS_COLOR = {
  published: "success",
  draft: "warning",
  cancelled: "default",
  in_progress: "blue",
};

const STATUS_TO_LABEL = {
  PUBLISHED: "Published",
  DRAFT: "Ready to Publish",
  IN_PROGRESS: "Draft",
};

export const EVENT_TYPES = [
  { value: "CONCERT", label: "Concert" },
  { value: "WORKSHOP", label: "Workshop" },
  { value: "CONFERENCE", label: "Conference" },
  { value: "FESTIVAL", label: "Festival" },
  { value: "SPORTS", label: "Sports" },
  { value: "EXHIBITION", label: "Exhibition" },
  { value: "SEMINAR", label: "Seminar" },
  { value: "OTHER", label: "Other" },
];

/** Copy for the "Show in listings" (link-only events) feature. */
export const SHOW_IN_LISTINGS_COPY = {
  label: "Show in listings",
  helperOn:
    "People can find this event in search, on the events page, on community pages and in featured spots.",
  helperOff:
    "This event is hidden from search and all listings. Anyone with the link can still open it and book.",
  helperDelay: "Changes can take up to 30 minutes to show on ShoUp.",
  tooltip:
    "Turn this off for invite-only or private events that you want to share yourself. The event page and booking still work for anyone with the link. To limit who can book, use Event Access.",
  switchAriaLabel: "Show this event in listings",
  badge: "Link only",
  badgeTooltip: "Hidden from listings. Opens only through its link.",
  valueOn: "Shown",
  valueOff: "Link only",
  reviewValueOn: "Yes",
  reviewValueOff: "No (link only)",
  reviewNoteOff:
    "This event will be link only. After it's published, copy its link from the event page and share it with the people you want to invite.",
  copiedTitle: "Link Copied",
  copiedListed: "Event share link copied to clipboard.",
  copiedLinkOnly:
    "Event link copied. This event is link only, so share it with the people you want to invite.",
  shareDisabledTooltip: "Publish the event to get a shareable link.",
  shareUnavailableTooltip:
    "Link isn't available yet. Refresh the page or try again later.",
  saveListingChanged:
    "Listing changes can take up to 30 minutes to show on ShoUp.",
  publishConfirmLinkOnly:
    "This event is link only. It won't appear in search or listings. Share its link to invite people.",
  filterPlaceholder: "Listing: All",
  filterAriaLabel: "Filter by listing",
  emptyLinkOnlyTitle: "No link-only events",
  emptyLinkOnlyBody:
    'Turn off "Show in listings" on an event to share it only by link.',
};

/** Listing filter options (URL values `shown` / `link_only`; cleared = all). */
export const LISTING_FILTER_OPTIONS = [
  { value: "shown", label: "Shown in listings" },
  { value: "link_only", label: "Link only" },
];

/** Maps the Listing filter URL value to the list request field (undefined = omit). */
export function listingFilterToShowInListings(listing) {
  if (listing === "shown") return true;
  if (listing === "link_only") return false;
  return undefined;
}

/** A missing or non-false value (older API responses) means the event is listed. */
export function isShownInListings(value) {
  return value !== false;
}

export const EVENT_ACCESS_OPTIONS = [
  { value: "PUBLIC", label: "Public" },
  { value: "MEMBERS_ONLY", label: "Members Only" },
];

export const STATE_CITY_MAP = {
  MAHARASHTRA: [
    "MUMBAI",
    "PUNE",
    "NAGPUR",
    "THANE",
    "NASHIK",
    "AURANGABAD",
    "SOLAPUR",
    "KOLHAPUR",
    "AMRAVATI",
    "NAVI MUMBAI",
    "SANGLI",
    "JALGAON",
    "AKOLA",
    "LATUR",
    "DHULE",
    "AHMEDNAGAR",
    "CHANDRAPUR",
    "PARBHANI",
    "JALNA",
    "BHIWANDI",
    "PANVEL",
    "SATARA",
    "YAVATMAL",
    "RATNAGIRI",
    "NANDURBAR",
    "WARDHA",
    "OSMANABAD",
    "GONDIA",
    "BEED",
    "HINGOLI",
    "WASHIM",
    "BULDHANA",
    "SINDHUDURG",
  ],
};

export const STATE_OPTIONS = Object.keys(STATE_CITY_MAP).map((s) => ({
  value: s,
  label: s,
}));

export function getCityOptions(state) {
  const cities = STATE_CITY_MAP[state] ?? [];
  return cities.map((c) => ({ value: c, label: c }));
}

export const DAYS_OF_WEEK = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

/**
 * Converts the Ant Design form values into the API payload for POST/PUT /v1/community-portal/event.
 *
 * All location fields live in the form under their exact API paths:
 *   location_mode        → "existing" | "new"
 *   location_id          → ID of the existing venue (when mode = "existing")
 *   location.*           → nested new-venue object (when mode = "new")
 *
 * Optional arrays (faq, things_to_know, partners, amenities, tags) are omitted when empty
 * so the API doesn't reject them for violating minItems: 1.
 *
 * @param {object}  values     - form.getFieldsValue(true)
 * @param {object}  userData   - from useAuthStore
 * @param {boolean} isEditMode - when true, sends community_id: null to explicitly remove association
 */
export function buildEventPayload(values, userData, isEditMode = false) {
  const amenities = (values.amenities ?? []).filter(
    (a) => a?.name && a?.icon_url
  );

  let locationPayload;
  if (values.location_mode === "existing") {
    locationPayload = { location_id: values.location_id };
  } else {
    const {
      media_url,
      amenities: locAmenities,
      geo_location,
      operating_hours,
      ...rest
    } = values.location ?? {};
    const filteredLocAmenities = (locAmenities ?? []).filter(
      (a) => a?.name && a?.icon_url
    );

    // Strip empty/null strings so BFF minLength validators don't reject them
    const cleanRest = Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== "" && v != null)
    );

    // Convert TimePicker dayjs values → "HH:mm" strings
    const toTimeStr = (v) =>
      !v ? undefined : typeof v === "string" ? v : v.format?.("HH:mm");
    const opHours = operating_hours
      ? {
          ...(toTimeStr(operating_hours.opening_time)
            ? { opening_time: toTimeStr(operating_hours.opening_time) }
            : {}),
          ...(toTimeStr(operating_hours.closing_time)
            ? { closing_time: toTimeStr(operating_hours.closing_time) }
            : {}),
          ...((operating_hours.days_of_week ?? []).length > 0
            ? { days_of_week: operating_hours.days_of_week }
            : {}),
        }
      : undefined;

    locationPayload = {
      location: {
        ...cleanRest,
        country_code: cleanRest.country_code || "IN",
        geo_location: {
          lat: Number(geo_location?.lat ?? 0),
          lng: Number(geo_location?.lng ?? 0),
        },
        is_primary: true,
        ...(opHours ? { operating_hours: opHours } : {}),
        ...(media_url
          ? { media: [{ type: "IMAGE", value: media_url, is_primary: true }] }
          : {}),
        ...(filteredLocAmenities.length > 0
          ? { amenities: filteredLocAmenities }
          : {}),
      },
    };
  }

  const validTags = (values.tags ?? []).filter(Boolean);
  const validFaq = (values.faq ?? []).filter((f) => f?.question && f?.answer);
  const validThingsToKnow = (values.things_to_know ?? []).filter(Boolean);
  const validPartners = (values.partners ?? []).filter(
    (p) => p?.name && p?.logo_url
  );

  return {
    // In edit mode send null explicitly so the core removes the association;
    // in create mode omit entirely if not selected.
    ...(values.community_id
      ? { community_id: values.community_id }
      : isEditMode
        ? { community_id: null }
        : {}),
    title: sanitizeText(values.title),
    description: sanitizeText(values.description),
    event_type: "CONCERT",
    is_public: values.event_access === "PUBLIC",
    is_featured: values.is_featured ?? false,
    event_access: values.event_access,
    hsn_code: "999723",
    // Core checks the vendor's permission and refuses changes after publish.
    charge_platform_fee: values.charge_platform_fee !== false,
    // Always sent so a published edit can switch it OFF -> ON -> OFF.
    show_in_listings: values.show_in_listings ?? true,
    ...(validTags.length > 0 ? { tags: validTags } : {}),
    start_date_time: values.start_date_time?.toISOString(),
    end_date_time: values.end_date_time?.toISOString(),
    ...locationPayload,
    contact_details: {
      email: sanitizeText(values.contact_email),
      phone_number: values.contact_phone,
    },
    organiser_details: {
      name: sanitizeText(values.organiser_name),
      logo_url: values.organiser_logo_url,
      ...(values.organiser_total_events != null
        ? { total_events_hosted: Number(values.organiser_total_events) }
        : {}),
      ...(values.organiser_since
        ? {
            hosting_since:
              typeof values.organiser_since === "string"
                ? values.organiser_since
                : values.organiser_since.format("YYYY-MM-DD"),
          }
        : {}),
    },
    media: (values.media ?? []).filter(Boolean).map((url, i) => ({
      type: "IMAGE",
      value: url,
      is_primary: i === 0,
    })),
    ...(amenities.length > 0 ? { amenities } : {}),
    activities: (values.activities ?? []).map((a) => ({
      name: sanitizeText(a.name),
      ...(sanitizeText(a.description)
        ? { description: sanitizeText(a.description) }
        : {}),
      ...(a.media_url
        ? { media: [{ type: "IMAGE", value: a.media_url, is_primary: true }] }
        : {}),
    })),
    ...(validFaq.length > 0
      ? {
          faq: validFaq.map((f) => ({
            question: sanitizeText(f.question),
            answer: sanitizeText(f.answer),
          })),
        }
      : {}),
    ...(values.tc_title
      ? {
          terms_and_conditions: {
            title: sanitizeText(values.tc_title),
            sub_title: sanitizeText(values.tc_subtitle),
            sections: (values.tc_sections ?? []).map((s) => ({
              heading: sanitizeText(s.heading),
              content: sanitizeText(s.content),
            })),
          },
        }
      : {}),
    ...(validThingsToKnow.length > 0
      ? { things_to_know: validThingsToKnow.map((t) => sanitizeText(t)) }
      : {}),
    ...(validPartners.length > 0
      ? {
          partners: validPartners.map((p) => ({
            name: sanitizeText(p.name),
            logo_url: p.logo_url,
          })),
        }
      : {}),
    schedule_info: (values.schedule_info ?? []).map((s) => ({
      date: s.date
        ? typeof s.date === "string"
          ? s.date
          : s.date.format("YYYY-MM-DD")
        : "",
      name: sanitizeText(s.name),
      value: s.value
        ? typeof s.value === "string"
          ? s.value
          : s.value.format("HH:mm") + " IST"
        : "",
    })),
    tickets: (values.tickets ?? []).map((t) => ({
      // Existing tiers carry product_id (+ vendor_sku) so the core matches them
      // on a published update; new tiers omit both and are created fresh.
      ...(t.product_id
        ? { product_id: t.product_id, vendor_sku: t.vendor_sku }
        : {}),
      name: sanitizeText(t.name),
      ...(sanitizeText(t.description)
        ? { description: sanitizeText(t.description) }
        : {}),
      price: Number(t.price),
      quantity: Number(t.quantity),
      max_concurrent_bookings: Number(t.max_concurrent_bookings ?? 10),
    })),
    audit: {
      created_by: userData?.email ?? userData?.phone_number,
    },
  };
}

/**
 * Maps GET /v1/community-portal/event/:id (setup detail) → Ant Design form values.
 * Spread the event object as-is; only override fields that need transformation
 * (date strings → dayjs, nested objects → flat form names, arrays → transformed shape).
 */
export function mapEventDetailToFormValues(event) {
  return {
    ...event,
    publication_status: event.publication_status ?? "DRAFT",
    // Date strings → dayjs for DatePicker
    start_date_time: event.start_date_time
      ? dayjs(event.start_date_time)
      : undefined,
    end_date_time: event.end_date_time ? dayjs(event.end_date_time) : undefined,
    // Nested → flat form field names
    contact_email: event.contact_details?.email,
    contact_phone: event.contact_details?.phone_number,
    organiser_name: event.organiser_details?.name,
    organiser_logo_url: event.organiser_details?.logo_url,
    organiser_total_events: event.organiser_details?.total_events_hosted,
    organiser_since: event.organiser_details?.hosting_since
      ? dayjs(event.organiser_details.hosting_since)
      : undefined,
    tc_title: event.terms_and_conditions?.title,
    tc_subtitle: event.terms_and_conditions?.sub_title,
    tc_sections: event.terms_and_conditions?.sections ?? [],
    // Arrays that need shape transforms
    activities: (event.activities ?? []).map((a) => ({
      name: a.name,
      description: a.description,
      media_url: getPrimaryMediaUrl(a.media),
    })),
    schedule_info: (event.schedule_info ?? []).map((s) => ({
      date: s.date ? dayjs(s.date) : undefined,
      name: s.name,
      value: s.value
        ? dayjs(`2000-01-01T${s.value.replace(/\s.*$/, "")}`)
        : undefined,
    })),
    media: (event.media ?? []).filter((m) => m.value).map((m) => m.value),
    // Location — spread location object, only add media_url shortcut
    location_mode: "existing",
    location_id: event.location?.location_id,
    hsn_code: "999723",
    charge_platform_fee: event.charge_platform_fee ?? true,
    // Older API responses have no field: treat as listed.
    show_in_listings: isShownInListings(event.show_in_listings),
    location: {
      ...event.location,
      media_url: getPrimaryMediaUrl(event.location?.media),
      operating_hours: event.location?.operating_hours
        ? {
            ...event.location.operating_hours,
            opening_time: event.location.operating_hours.opening_time
              ? dayjs(
                  `2000-01-01T${event.location.operating_hours.opening_time}`
                )
              : undefined,
            closing_time: event.location.operating_hours.closing_time
              ? dayjs(
                  `2000-01-01T${event.location.operating_hours.closing_time}`
                )
              : undefined,
          }
        : undefined,
    },
  };
}

/**
 * Prepares form values for cloning a published event into a new draft.
 * Identical to mapEventDetailToFormValues but with dates and schedules cleared
 * so the user is forced to set new ones.
 */
export function mapEventDetailToCloneValues(event) {
  const base = mapEventDetailToFormValues(event);
  return {
    ...base,
    start_date_time: undefined,
    end_date_time: undefined,
    schedule_info: [],
    publication_status: "IN_PROGRESS",
  };
}

export const STATUS_OPTIONS = [
  { value: "PUBLISHED", label: "Published" },
  { value: "DRAFT", label: "Ready to Publish" },
  { value: "IN_PROGRESS", label: "Draft" },
];

/** Normalises a raw portal list API row into a flat shape the list page uses. */
export function rowFromPortalList(r) {
  const media = r.media ?? [];
  const img =
    media.find((m) => m.type === "IMAGE" && m.is_primary) ??
    media.find((m) => m.type === "IMAGE");
  const venueName =
    r.venue_name ??
    r.location_name ??
    ([r.city, r.state].filter(Boolean).join(", ") ||
      (r.location_id ? "Saved venue" : "Venue TBA"));
  return {
    id: r.event_id,
    name: r.event_name || r.title || "",
    startDateTime: r.start_date_time,
    endDateTime: r.end_date_time,
    eventType: r.event_type,
    status: r.publication_status,
    communityId: r.community_id ?? null,
    maxAttendees: null,
    imageUrl: img?.value ?? null,
    location: { venueName },
    tickets: [],
    category: r.event_type,
    description: r.description ?? "",
    waitlistEnabled: false,
    organiser_details: r.organiser_details ?? null,
    slug: r.slug ?? null,
    chargePlatformFee: r.charge_platform_fee !== false,
    showInListings: isShownInListings(r.show_in_listings),
  };
}

/**
 * Ant Design table columns for the Events list page.
 * Accepts action handlers so the page component stays declarative.
 */
export function getEventListColumns({ handlePublish, handleClone }) {
  return [
    {
      title: "Event Name",
      key: "event",
      align: "center",
      render: (_, e) => (
        <div>
          {e.status === "IN_PROGRESS" ? (
            <span className="font-bold">{e.name}</span>
          ) : (
            <Link
              to={`/shoup-community/events/${e.id}`}
              className="font-bold hover:underline"
            >
              {e.name}
            </Link>
          )}
        </div>
      ),
    },
    {
      title: "Organiser",
      key: "organiser",
      align: "center",
      render: (_, e) => (
        <Row align="middle" gutter={32}>
          {e.organiser_details?.logo_url && (
            <Col>
              <img
                src={e.organiser_details.logo_url}
                alt={e.organiser_details.name}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            </Col>
          )}
          <Col>
            <span style={{ fontSize: 13 }}>{e.organiser_details?.name}</span>
          </Col>
        </Row>
      ),
    },
    {
      title: "Date & Time",
      key: "startDateTime",
      align: "center",
      render: (record) => (
        <span className="text-muted-foreground text-sm">
          {record.startDateTime && !isNaN(new Date(record.startDateTime))
            ? format(new Date(record.startDateTime), "MMM d, yyyy • h:mm a")
            : "—"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (record) => {
        const statusTag = (
          <Tag
            color={EVENT_STATUS_COLOR[record.status.toLowerCase()]}
            className="capitalize"
          >
            {STATUS_TO_LABEL[record.status] ?? getPascalCase(record.status)}
          </Tag>
        );
        // Listed events keep today's markup; link-only rows add the badge.
        if (record.showInListings !== false) return statusTag;
        return (
          <Space size={4} wrap style={{ justifyContent: "center" }}>
            {statusTag}
            <LinkOnlyBadge />
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => {
        const isPublished = record.status === "PUBLISHED";

        const moreItems = [
          ...(record.slug
            ? [
                {
                  key: "share",
                  label: "Copy share link",
                  icon: <Share2 size={13} />,
                  onClick: () => {
                    navigator.clipboard.writeText(
                      getEventShareUrl(record.slug)
                    );
                    renderSuccessNotifications(
                      getShareCopiedNotification(record.showInListings)
                    );
                  },
                },
              ]
            : []),
          {
            key: "clone",
            label: "Clone event",
            icon: <Copy size={13} />,
            onClick: () => handleClone(record.id),
          },
        ];

        return (
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Link to={`/shoup-community/events/${record.id}/edit`}>
              <Button type="primary" size="small" icon={<Pencil size={12} />}>
                Edit
              </Button>
            </Link>

            {isPublished && (
              <Dropdown menu={{ items: moreItems }} trigger={["click"]}>
                <Button
                  size="small"
                  icon={<ChevronDown size={12} />}
                  iconPosition="end"
                >
                  More
                </Button>
              </Dropdown>
            )}

            {record.status === "DRAFT" && (
              <Tooltip title="This action is irreversible">
                <Button
                  size="small"
                  ghost
                  type="primary"
                  icon={<Globe size={12} />}
                  onClick={() => handlePublish(record)}
                >
                  Publish
                </Button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
}

// Website host for share links. Optional VITE_WEBSITE_URL (no trailing slash)
// lets local QA point links elsewhere; unset keeps the production host.
const WEBSITE_BASE_URL = (
  import.meta.env.VITE_WEBSITE_URL || "https://app.weshoup.com"
).replace(/\/+$/, "");

export function getEventShareUrl(slug) {
  return `${WEBSITE_BASE_URL}/events/${slug}`;
}

/** Toast shown after copying an event link; text depends on the listing flag. */
export function getShareCopiedNotification(showInListings) {
  return {
    title: SHOW_IN_LISTINGS_COPY.copiedTitle,
    message:
      showInListings === false
        ? SHOW_IN_LISTINGS_COPY.copiedLinkOnly
        : SHOW_IN_LISTINGS_COPY.copiedListed,
  };
}

/** Purple "Link only" tag with a focusable tooltip host (list + dashboard). */
export function LinkOnlyBadge({ withTooltip = true }) {
  const tag = (
    <Tag
      color="purple"
      icon={<Link2 size={12} aria-hidden className="inline mr-1" />}
      style={{ marginInlineEnd: 0 }}
    >
      {SHOW_IN_LISTINGS_COPY.badge}
    </Tag>
  );
  if (!withTooltip) return tag;
  return (
    <Tooltip title={SHOW_IN_LISTINGS_COPY.badgeTooltip}>
      <span tabIndex={0} className="inline-flex">
        {tag}
      </span>
    </Tooltip>
  );
}

/**
 * True when the event has at least one ticket tier and every tier has a price
 * of 0. Free tickets carry no platform fee, so there is nothing to waive.
 */
export function areAllTicketsFree(tickets) {
  return (
    Array.isArray(tickets) &&
    tickets.length > 0 &&
    tickets.every(
      (t) =>
        t &&
        t.price !== "" &&
        t.price !== null &&
        t.price !== undefined &&
        Number(t.price) === 0
    )
  );
}

export function getEventMinPrice(event) {
  const tickets = event.tickets ?? [];
  if (!tickets.length) return null;
  const min = Math.min(...tickets.map((t) => t.price));
  return min === 0 ? "Free" : `From ₹${min}`;
}

export const HSN_CODE_OPTIONS = [
  {
    label: "18% Tax",
    description: "Eighteen percent tax",
    value: "999723",
  },
];

export function serializeDraftFormValues(values) {
  const out = { ...values };
  // TimePicker stores a dayjs object; JSON.stringify can produce null for it.
  // Format it as a plain "HH:mm" string so deserialize can reuse the same
  // approach as mapEventDetailToFormValues.
  if (Array.isArray(out.schedule_info)) {
    out.schedule_info = out.schedule_info.map((s) => ({
      ...s,
      value: dayjs.isDayjs(s.value) ? s.value.format("HH:mm") : s.value,
    }));
  }
  return JSON.parse(JSON.stringify(out));
}

export function deserializeDraftFormValues(data) {
  const result = { ...data };

  // Drafts saved before "Show in listings" existed have no key: treat as ON.
  result.show_in_listings = isShownInListings(result.show_in_listings);

  if (result.start_date_time)
    result.start_date_time = dayjs(result.start_date_time);
  if (result.end_date_time) result.end_date_time = dayjs(result.end_date_time);
  if (result.organiser_since)
    result.organiser_since = dayjs(result.organiser_since);

  if (Array.isArray(result.schedule_info)) {
    result.schedule_info = result.schedule_info.map((s) => ({
      ...s,
      date: s.date ? dayjs(s.date) : undefined,
      value: s.value
        ? dayjs(`2000-01-01T${s.value.replace(/\s.*$/, "")}`)
        : undefined,
    }));
  }

  return result;
}
